import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, ChevronDown, ChevronRight, HelpCircle, X, Loader2, Upload, Download, AlertCircle, CheckCircle, FileSpreadsheet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import {
    createCategory,
    getCategories,
    updateCategory,
    deleteCategory,
    createSubcategory,
    getSubcategoriesByCategory,
    updateSubcategory,
    deleteSubcategory,
    clearError,
    clearSuccess,
    downloadSupportSampleExcel,
    checkSupportBulkFormat,
    bulkUploadSupport
} from '../../redux/masterSlice';
import MasterLabel from '../../components/common/MasterLabel';
import useCanManage from '../../hooks/useCanManage';

// WHO a category / sub-category is offered to. Mirrors the backend's
// SUPPORT_AUDIENCES (Backend/config/supportAudiences.js): the raiser kinds of
// config/supportRaisers.js plus 'all'. Grouped by the app the raiser opens,
// which is how the form lays the checkboxes out.
const AUDIENCE_GROUPS = [
    { key: 'patient_app', labelKey: 'supportPage.audience_patient_app', fallback: 'Patient app (Mitra One Care)', tags: ['moc_patient'] },
    { key: 'tenant_app', labelKey: 'supportPage.audience_tenant_app', fallback: 'Tenant app', tags: ['tenant_patient', 'doctor', 'tenant_admin', 'staff'] },
];
const AUDIENCE_TAGS = AUDIENCE_GROUPS.flatMap((g) => g.tags);
const AUDIENCE_LABELS = {
    moc_patient: ['supportPage.audience_moc_patient', 'MOC patient'],
    tenant_patient: ['supportPage.audience_tenant_patient', 'Hospital patient'],
    doctor: ['supportPage.audience_doctor', 'Doctor'],
    tenant_admin: ['supportPage.audience_tenant_admin', 'Tenant admin'],
    staff: ['supportPage.audience_staff', 'Staff'],
    all: ['supportPage.audience_all', 'Everyone'],
};
// Aliases the backend also accepts: values that predate the raiser-kind tags
// (the legacy `role` mirror, the per-job staff tags, plain 'admin'). Mapped on
// read so a row not yet rewritten by the migration still shows correctly.
const AUDIENCE_ALIASES = {
    patient: ['moc_patient', 'tenant_patient'],
    both: ['doctor', 'moc_patient', 'tenant_patient'],
    admin: ['tenant_admin'],
    pharmacist: ['staff'],
    lab_technician: ['staff'],
    radiologist: ['staff'],
    care_taker: ['staff'],
};

// Clean any tag list into canonical order, resolving aliases and dropping
// unknowns. 'all' collapses the list to itself.
const normalizeAudiences = (list) => {
    const expanded = (Array.isArray(list) ? list : [])
        .map((tag) => String(tag || '').trim().toLowerCase())
        .flatMap((tag) => AUDIENCE_ALIASES[tag] || [tag]);
    if (expanded.includes('all')) return ['all'];
    return AUDIENCE_TAGS.filter((tag) => expanded.includes(tag));
};

// What a category / sub-category row is for: prefer the `audiences` array,
// fall back to the legacy `role` string for rows that only carry the mirror.
const readAudiences = (row) => {
    const fromArray = normalizeAudiences(row?.audiences);
    if (fromArray.length) return fromArray;
    const legacy = row?.role || row?.viewableBy;
    return normalizeAudiences(legacy ? [legacy] : ['both']);
};

// Tags a sub-category may carry: everything when the parent is 'all',
// otherwise exactly the parent's own tags (the server enforces the same).
const allowedForParent = (parentAudiences) =>
    parentAudiences.includes('all') ? AUDIENCE_TAGS : parentAudiences;

// Compact read-only tag chips shown on the category / sub-category cards.
const AudienceChips = ({ audiences, testIdPrefix }) => {
    const { t } = useTranslation('supportPage');
    return (
        <span className="inline-flex items-center flex-wrap gap-1" data-testid={`${testIdPrefix}-audiences`}>
            {audiences.map((tag) => (
                <span
                    key={tag}
                    className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
                        tag === 'all'
                            ? 'bg-[#FBF1E4] text-[#7A5320] border-[#D7A05C]/60'
                            : 'bg-[#E7F5F4] text-[#2D8A8A] border-[#4ABDBD]/50'
                    }`}
                    data-testid={`${testIdPrefix}-audience-${tag}`}
                >
                    {t(...(AUDIENCE_LABELS[tag] || [tag, tag]))}
                </span>
            ))}
        </span>
    );
};

// Checkbox groups for picking audiences. `allowedTags` limits what is offered
// (a sub-category may only pick from its parent); `allowAll` shows the
// "Everyone" switch, which disables the individual boxes while it is on.
const AudienceCheckboxes = ({ value, onChange, allowedTags, allowAll, disabled, testIdPrefix }) => {
    const { t } = useTranslation('supportPage');
    const everyone = value.includes('all');
    const toggleTag = (tag, checked) => {
        const base = value.filter((v) => v !== 'all' && v !== tag);
        onChange(normalizeAudiences(checked ? [...base, tag] : base));
    };
    return (
        <div className="space-y-3">
            {AUDIENCE_GROUPS.map((group) => {
                const tags = group.tags.filter((tag) => allowedTags.includes(tag));
                if (!tags.length) return null;
                return (
                    <div key={group.key} className="rounded-xl border border-gray-200 p-3" data-testid={`${testIdPrefix}-group-${group.key}`}>
                        <p className="text-[11px] font-extrabold uppercase tracking-widest text-[#2D8A8A] mb-2">
                            {t(group.labelKey, group.fallback)}
                        </p>
                        <div className="flex flex-wrap gap-x-5 gap-y-2">
                            {tags.map((tag) => (
                                <label key={tag} className={`flex items-center gap-2 text-sm text-gray-700 ${disabled || everyone ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                                    <input
                                        type="checkbox"
                                        checked={everyone || value.includes(tag)}
                                        disabled={disabled || everyone}
                                        onChange={(e) => toggleTag(tag, e.target.checked)}
                                        className="w-4 h-4 accent-[#4ABDBD] rounded"
                                        data-testid={`${testIdPrefix}-${tag}-input`}
                                    />
                                    <span>{t(...AUDIENCE_LABELS[tag])}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                );
            })}
            {allowAll && (
                <label className={`flex items-center gap-2 text-sm font-semibold text-gray-800 px-1 ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                        type="checkbox"
                        checked={everyone}
                        disabled={disabled}
                        onChange={(e) => onChange(e.target.checked ? ['all'] : [])}
                        className="w-4 h-4 accent-[#D7A05C] rounded"
                        data-testid={`${testIdPrefix}-all-input`}
                    />
                    <span>{t(...AUDIENCE_LABELS.all)}</span>
                </label>
            )}
        </div>
    );
};

const EMPTY_CATEGORY_FORM = { name: '', audiences: [] };
const EMPTY_SUBCATEGORY_FORM = { name: '', description: '', audiences: [] };

const Support = () => {
    const { t } = useTranslation('supportPage');
    const dispatch = useDispatch();
    // Write-side controls hide without the manage key — the API refuses
    // them anyway (hooks/useCanManage.js).
    const canManage = useCanManage('masters.manage');
    const { categories, subcategories, loading, categoryLoading, subcategoryLoading, error, success, supportDownloadLoading, supportBulkUploadLoading } = useSelector((state) => state.master);

    // Bulk upload state — single workbook with two sheets (Categories + SubCategories).
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
    const [bulkSelectedFile, setBulkSelectedFile] = useState(null);
    const [bulkFileError, setBulkFileError] = useState('');
    const [bulkFormatChecking, setBulkFormatChecking] = useState(false);
    const [bulkFormatValid, setBulkFormatValid] = useState(false);
    const [bulkFormatInfo, setBulkFormatInfo] = useState(null);
    const [bulkUploadResult, setBulkUploadResult] = useState(null);
    const bulkFileInputRef = useRef(null);

    const handleDownloadSupportSample = async () => {
        try {
            await dispatch(downloadSupportSampleExcel()).unwrap();
        } catch (err) {
            toast.warn(err?.error || 'Failed to download support sample template');
        }
    };

    const handleOpenBulkUpload = () => {
        setBulkSelectedFile(null);
        setBulkUploadResult(null);
        setBulkFileError('');
        setBulkFormatValid(false);
        setBulkFormatChecking(false);
        setBulkFormatInfo(null);
        setShowBulkUploadModal(true);
    };

    const handleCloseBulkUpload = () => {
        setShowBulkUploadModal(false);
        setBulkSelectedFile(null);
        setBulkUploadResult(null);
        setBulkFileError('');
        setBulkFormatValid(false);
        setBulkFormatChecking(false);
        setBulkFormatInfo(null);
    };

    const handleBulkFileSelect = async (e) => {
        const file = e.target.files?.[0];
        setBulkFileError('');
        setBulkUploadResult(null);
        setBulkFormatValid(false);
        setBulkFormatInfo(null);
        if (!file) { setBulkSelectedFile(null); return; }

        const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        if (!['.xlsx', '.xls'].includes(ext)) {
            setBulkFileError('Please upload a valid Excel file (.xlsx or .xls)');
            setBulkSelectedFile(null);
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setBulkFileError('File size must not exceed 5MB');
            setBulkSelectedFile(null);
            return;
        }

        setBulkSelectedFile(file);
        setBulkFormatChecking(true);
        try {
            const info = await dispatch(checkSupportBulkFormat(file)).unwrap();
            setBulkFormatValid(true);
            setBulkFormatInfo(info);
        } catch (err) {
            setBulkFormatValid(false);
            setBulkFileError(err?.error || 'Invalid Excel format');
        } finally {
            setBulkFormatChecking(false);
        }
    };

    const handleBulkUploadSubmit = async () => {
        if (!bulkSelectedFile) { setBulkFileError('Please select a file to upload'); return; }
        if (!bulkFormatValid) { setBulkFileError('Please fix the file format errors first'); return; }
        try {
            const result = await dispatch(bulkUploadSupport(bulkSelectedFile)).unwrap();
            setBulkUploadResult(result);
            // Re-fetch categories so the freshly created rows appear in the list.
            dispatch(getCategories());
        } catch (err) {
            toast.warn(err?.error || 'Failed to bulk upload support');
        }
    };

    const handleBulkDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
    const handleBulkDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const file = e.dataTransfer.files?.[0];
        if (file && bulkFileInputRef.current) {
            const dt = new DataTransfer();
            dt.items.add(file);
            bulkFileInputRef.current.files = dt.files;
            handleBulkFileSelect({ target: { files: dt.files } });
        }
    };
    
    // Ref to track initial API call
    const hasInitialFetch = useRef(false);
    
    const [expandedCategories, setExpandedCategories] = useState(new Set());
    
    // Modal states
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [showSubCategoryModal, setShowSubCategoryModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [editingSubCategory, setEditingSubCategory] = useState(null);
    const [parentCategoryId, setParentCategoryId] = useState(null);
    const [parentCategory, setParentCategory] = useState(null);
    
    // Form states
    // `audiences` is the tag array the API stores; the legacy 3-way `role`
    // is derived server-side and never sent from here.
    const [categoryForm, setCategoryForm] = useState(EMPTY_CATEGORY_FORM);
    const [subCategoryForm, setSubCategoryForm] = useState(EMPTY_SUBCATEGORY_FORM);
    const [errors, setErrors] = useState({
        categoryName: '',
        categoryAudiences: '',
        subCategoryName: '',
        subCategoryDescription: '',
        subCategoryAudiences: ''
    });

    // Fetch categories on mount - only once using ref
    useEffect(() => {
        if (!hasInitialFetch.current) {
            hasInitialFetch.current = true;
            dispatch(getCategories());
        }
    }, [dispatch]);

    // Debug: Log categories when they change
    useEffect(() => {
        console.log('Categories from Redux:', categories);
        console.log('Is array?', Array.isArray(categories));
        if (categories && !Array.isArray(categories)) {
            console.log('Categories type:', typeof categories);
            console.log('Categories value:', categories);
        }
    }, [categories]);

    // Handle success messages
    useEffect(() => {
        if (success) {
            // Refresh categories after successful operation
            dispatch(getCategories());
            dispatch(clearSuccess());
        }
    }, [success, dispatch]);

    // Handle error messages
    useEffect(() => {
        if (error) {
            toast.warn(error);
            dispatch(clearError());
        }
    }, [error, dispatch, t]);

    // Load subcategories when category is expanded (accordion: only one open)
    const handleCategoryExpand = async (category) => {
        const categoryId = category.categoryId || category.id;
        const newExpanded = new Set();
        if (!expandedCategories.has(categoryId)) {
            newExpanded.add(categoryId);
            // Fetch subcategories if not already loaded
            if (!subcategories[categoryId]) {
                await dispatch(getSubcategoriesByCategory({ categoryId }));
            }
        }
        setExpandedCategories(newExpanded);
    };

    // Category CRUD operations
    const handleAddCategory = () => {
        setEditingCategory(null);
        setCategoryForm(EMPTY_CATEGORY_FORM);
        setErrors(prev => ({ ...prev, categoryName: '', categoryAudiences: '' }));
        setShowCategoryModal(true);
    };

    const handleEditCategory = (category) => {
        setEditingCategory(category);
        setCategoryForm({
            name: category.name,
            audiences: readAudiences(category)
        });
        setErrors(prev => ({ ...prev, categoryName: '', categoryAudiences: '' }));
        setShowCategoryModal(true);
    };

    const handleDeleteCategory = async (category) => {
        const result = await Swal.fire({
            title: t('supportPage.delete_category_title'),
            text: t('supportPage.delete_category_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#9ca3af',
            confirmButtonText: t('supportPage.confirm_delete'),
            cancelButtonText: t('supportPage.confirm_cancel')
        });

        if (result.isConfirmed) {
            try {
                const categoryId = category.categoryId || category.id;
                await dispatch(deleteCategory(categoryId)).unwrap();
                toast.success(t('supportPage.category_deleted_text'));
            } catch (error) {
                const errorMsg = error?.error?.en || error?.error?.fr || error?.error || error?.message?.en || error?.message || t('supportPage.delete_error');
                toast.warn(errorMsg);
            }
        }
    };

    // Validate category name
    const validateCategoryName = (name) => {
        // Sanitize input - remove dangerous characters
        const sanitized = name.replace(/[<>]/g, '');
        
        if (!sanitized.trim()) {
            return t('supportPage.category_name_required');
        }
        if (/^\s+$/.test(sanitized)) {
            return t('supportPage.category_name_only_whitespace', 'Category name cannot contain only whitespace');
        }
        if (sanitized.trim().length < 2) {
            return t('supportPage.category_name_min_length', 'Category name must be at least 2 characters');
        }
        if (sanitized.length > 50) {
            return t('supportPage.category_name_max_length', 'Category name cannot exceed 50 characters');
        }
        if (!/^[A-Za-z0-9\s\-_]+$/.test(sanitized)) {
            return t('supportPage.category_name_invalid_chars', 'Category name can only contain letters, numbers, spaces, hyphens, and underscores');
        }
        return '';
    };

    // Validate subcategory name
    const validateSubCategoryName = (name) => {
        // Sanitize input - remove dangerous characters
        const sanitized = name.replace(/[<>]/g, '');
        
        if (!sanitized.trim()) {
            return t('supportPage.subcategory_name_required', 'Subcategory name is required');
        }
        if (/^\s+$/.test(sanitized)) {
            return t('supportPage.subcategory_name_only_whitespace', 'Subcategory name cannot contain only whitespace');
        }
        if (sanitized.trim().length < 2) {
            return t('supportPage.subcategory_name_min_length', 'Subcategory name must be at least 2 characters');
        }
        if (sanitized.length > 50) {
            return t('supportPage.subcategory_name_max_length', 'Subcategory name cannot exceed 50 characters');
        }
        if (!/^[A-Za-z0-9\s\-_]+$/.test(sanitized)) {
            return t('supportPage.subcategory_name_invalid_chars', 'Subcategory name can only contain letters, numbers, spaces, hyphens, and underscores');
        }
        return '';
    };

    // Validate subcategory description
    const validateSubCategoryDescription = (description) => {
        // Sanitize input - remove dangerous characters
        const sanitized = description.replace(/[<>]/g, '');
        
        if (!sanitized.trim()) {
            return t('supportPage.description_required', 'Description is required');
        }
        if (/^\s+$/.test(sanitized)) {
            return t('supportPage.description_only_whitespace', 'Description cannot contain only whitespace');
        }
        if (sanitized.trim().length < 5) {
            return t('supportPage.description_min_length', 'Description must be at least 5 characters');
        }
        if (sanitized.length > 200) {
            return t('supportPage.description_max_length', 'Description cannot exceed 200 characters');
        }
        return '';
    };

    const audienceRequiredMessage = () => t('supportPage.audience_required', 'Select at least one audience');

    const handleSaveCategory = async () => {
        const nameError = validateCategoryName(categoryForm.name);
        const audienceError = categoryForm.audiences.length ? '' : audienceRequiredMessage();
        if (nameError || audienceError) {
            setErrors(prev => ({ ...prev, categoryName: nameError, categoryAudiences: audienceError }));
            toast.warn(nameError || audienceError);
            return;
        }
        
        setErrors(prev => ({ ...prev, categoryName: '', categoryAudiences: '' }));

        try {
            if (editingCategory) {
                // Update existing category
                const categoryId = editingCategory.categoryId || editingCategory.id;
                await dispatch(updateCategory({
                    categoryId,
                    name: categoryForm.name.trim(),
                    audiences: categoryForm.audiences
                })).unwrap();

                toast.success(t('supportPage.category_updated_text'));
            } else {
                // Create new category
                await dispatch(createCategory({
                    name: categoryForm.name.trim(),
                    audiences: categoryForm.audiences
                })).unwrap();

                toast.success(t('supportPage.category_created_text'));
            }

            setShowCategoryModal(false);
            setCategoryForm(EMPTY_CATEGORY_FORM);
            setEditingCategory(null);
        } catch (error) {
            // Error handled by useEffect
        }
    };

    // SubCategory CRUD operations
    const handleAddSubCategory = (category) => {
        const categoryId = category.categoryId || category.id;
        setParentCategoryId(categoryId);
        setParentCategory(category);
        setEditingSubCategory(null);
        // A new sub-category starts with its parent's audiences — the widest
        // set it is allowed to have — so the common case is one click fewer.
        setSubCategoryForm({ ...EMPTY_SUBCATEGORY_FORM, audiences: readAudiences(category) });
        setErrors({
            subCategoryName: '',
            subCategoryDescription: '',
            subCategoryAudiences: ''
        });
        setShowSubCategoryModal(true);
    };

    const handleEditSubCategory = (subCategory, category) => {
        const categoryId = category.categoryId || category.id;
        setParentCategoryId(categoryId);
        setParentCategory(category);
        setEditingSubCategory(subCategory);
        // Clip to what the parent allows: a tag the parent no longer has is
        // not offered as a checkbox, so it could never be unticked otherwise.
        const parentAudiences = readAudiences(category);
        const stored = readAudiences(subCategory);
        const audiences = parentAudiences.includes('all')
            ? stored
            : allowedForParent(parentAudiences).filter((tag) => stored.includes('all') || stored.includes(tag));
        setSubCategoryForm({
            name: subCategory.name,
            description: subCategory.description,
            audiences
        });
        setErrors({
            subCategoryName: '',
            subCategoryDescription: '',
            subCategoryAudiences: ''
        });
        setShowSubCategoryModal(true);
    };

    const handleDeleteSubCategory = async (subCategory, category) => {
        const result = await Swal.fire({
            title: t('supportPage.delete_subcategory_title'),
            text: t('supportPage.delete_subcategory_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#9ca3af',
            confirmButtonText: t('supportPage.confirm_delete'),
            cancelButtonText: t('supportPage.confirm_cancel')
        });

        if (result.isConfirmed) {
            try {
                const subcategoryId = subCategory.subCategoryId || subCategory.subcategoryId || subCategory.id;
                await dispatch(deleteSubcategory(subcategoryId)).unwrap();
                toast.success(t('supportPage.subcategory_deleted_text'));

                // Refresh subcategories for this category
                const categoryId = category.categoryId || category.id;
                await dispatch(getSubcategoriesByCategory({ categoryId }));
            } catch (error) {
                const errorMsg = error?.error?.en || error?.error?.fr || error?.error || error?.message?.en || error?.message || t('supportPage.delete_error');
                toast.warn(errorMsg);
            }
        }
    };

    const handleSaveSubCategory = async () => {
        const nameError = validateSubCategoryName(subCategoryForm.name);
        const descriptionError = validateSubCategoryDescription(subCategoryForm.description);
        const audienceError = subCategoryForm.audiences.length ? '' : audienceRequiredMessage();
        
        if (nameError || descriptionError || audienceError) {
            setErrors({
                subCategoryName: nameError,
                subCategoryDescription: descriptionError,
                subCategoryAudiences: audienceError
            });
            toast.warn(nameError || descriptionError || audienceError || t('supportPage.subcategory_validation_error'));
            return;
        }
        
        setErrors({
            subCategoryName: '',
            subCategoryDescription: '',
            subCategoryAudiences: ''
        });

        try {
            if (editingSubCategory) {
                // Update existing subcategory
                const subcategoryId = editingSubCategory.subCategoryId || editingSubCategory.subcategoryId || editingSubCategory.id;
                await dispatch(updateSubcategory({
                    subcategoryId,
                    categoryId: parentCategoryId,
                    name: subCategoryForm.name.trim(),
                    description: subCategoryForm.description.trim(),
                    audiences: subCategoryForm.audiences
                })).unwrap();

                toast.success(t('supportPage.subcategory_updated_text'));
            } else {
                // Create new subcategory
                await dispatch(createSubcategory({
                    categoryId: parentCategoryId,
                    name: subCategoryForm.name.trim(),
                    description: subCategoryForm.description.trim(),
                    audiences: subCategoryForm.audiences
                })).unwrap();

                toast.success(t('supportPage.subcategory_created_text'));
            }

            setShowSubCategoryModal(false);
            setSubCategoryForm(EMPTY_SUBCATEGORY_FORM);
            setEditingSubCategory(null);
            setParentCategoryId(null);
            setParentCategory(null);

            // Refresh subcategories for this category
            if (parentCategoryId) {
                await dispatch(getSubcategoriesByCategory({ categoryId: parentCategoryId }));
            }
        } catch (error) {
            // Error handled by useEffect
        }
    };

    // Get subcategories for a category
    const getCategorySubcategories = (category) => {
        const categoryId = category.categoryId || category.id;
        return subcategories[categoryId] || [];
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64" data-testid="support-master-loading-container">
                <Loader2 className="animate-spin text-[#4ABDBD]" size={32} data-testid="support-master-loading-icon" />
                <span className="ml-2 text-gray-600" data-testid="support-master-loading-text">{t('supportPage.loading')}</span>
            </div>
        );
    }

    const safeCategories = Array.isArray(categories) ? categories : [];
    // What the open sub-category modal may offer (see AudienceCheckboxes).
    const parentAudiences = parentCategory ? readAudiences(parentCategory) : ['all'];

    return (
        <div className="bg-white rounded-2xl p-6 shadow-md" data-testid="support-master-container">
            {/* Header */}
            <div className="flex items-center justify-between mb-6" data-testid="support-master-header">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent" data-testid="support-master-title">
                        {t('supportPage.title')}
                    </h1>
                    <p className="text-gray-500 mt-1" data-testid="support-master-description">{t('supportPage.description')}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {canManage && (
                    <button
                        onClick={handleAddCategory}
                        disabled={categoryLoading}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="support-master-add-category-button"
                    >
                        {categoryLoading ? (
                            <Loader2 className="animate-spin" size={20} data-testid="support-master-add-category-loading-icon" />
                        ) : (
                            <Plus size={20} data-testid="support-master-add-category-icon" />
                        )}
                        {t('supportPage.add_category')}
                    </button>
                    )}
                    {canManage && (
                    <button
                        onClick={handleOpenBulkUpload}
                        className="group flex items-center gap-2 px-4 py-2 border-2 border-[#3AA8A8] text-[#3AA8A8] rounded-lg hover:bg-[#3AA8A8] hover:text-white transition-all shadow-sm hover:shadow-md cursor-pointer"
                        title="Bulk Upload"
                        data-testid="support-master-bulk-upload-button"
                    >
                        <Upload size={18} />
                        <span className="hidden sm:inline">Bulk Upload</span>
                    </button>
                    )}
                </div>
            </div>

            {/* Categories List */}
            <div className="space-y-4" data-testid="support-master-categories-list">
                {safeCategories.length === 0 ? (
                    <div className="text-center py-12 text-gray-500" data-testid="support-master-no-categories-container">
                        <HelpCircle size={48} className="mx-auto mb-4 text-gray-300" data-testid="support-master-no-categories-icon" />
                        <p data-testid="support-master-no-categories-text">{t('supportPage.no_categories')}</p>
                    </div>
                ) : (
                    safeCategories.map((category) => {
                        const categoryId = category.categoryId || category.id;
                        const isExpanded = expandedCategories.has(categoryId);
                        const categorySubcategories = getCategorySubcategories(category);

  return (
                            <div
                                key={categoryId}
                                className="border-2 border-gray-200 rounded-lg overflow-hidden hover:border-[#4ABDBD] transition-all"
                                data-testid={`support-master-category-card-${categoryId}`}
                            >
                                {/* Category Header */}
                                <div
                                    className="bg-gradient-to-r from-[#4ABDBD]/10 to-[#3A9B9B]/10 p-4 flex items-center justify-between cursor-pointer"
                                    onClick={() => handleCategoryExpand(category)}
                                    data-testid={`support-master-category-header-${categoryId}`}
                                >
                                    <div className="flex items-center gap-3 flex-1">
                                        <h3 className="font-semibold text-gray-800 flex-1" data-testid={`support-master-category-name-${categoryId}`}>
                                            <MasterLabel type="masterSupportCategory" id={category.categoryId || category.id} field="name" fallback={category.name} />
                                        </h3>
                                        <span className="flex items-center gap-1.5 mr-2 text-xs text-gray-500" data-testid={`support-master-category-viewable-${categoryId}`}>
                                            <span className="hidden sm:inline">{t('supportPage.audiences', 'Audiences')}:</span>
                                            <AudienceChips audiences={readAudiences(category)} testIdPrefix={`support-master-category-${categoryId}`} />
                                        </span>
                                        <span className="text-sm text-gray-500 bg-white px-2 py-1 rounded" data-testid={`support-master-category-count-${categoryId}`}>
                                            {category.subCategoryCount} {t('supportPage.subcategories')}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2 ml-4">
                                        {canManage && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleEditCategory(category); }}
                                            disabled={categoryLoading}
                                            className="p-2 text-[#4ABDBD] hover:bg-white/50 rounded transition-colors disabled:opacity-50"
                                            title={t('supportPage.edit_category_tooltip')}
                                            data-testid={`support-master-edit-category-button-${categoryId}`}
                                        >
                                            <Edit2 size={18} data-testid={`support-master-edit-category-icon-${categoryId}`} />
                                        </button>
                                        )}
                                        {canManage && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleDeleteCategory(category); }}
                                            disabled={categoryLoading}
                                            className="p-2 text-red-500 hover:bg-white/50 rounded transition-colors disabled:opacity-50"
                                            title={t('supportPage.delete_category_tooltip')}
                                            data-testid={`support-master-delete-category-button-${categoryId}`}
                                        >
                                            <Trash2 size={18} data-testid={`support-master-delete-category-icon-${categoryId}`} />
                                        </button>
                                        )}
                                    </div>
                                </div>

                                {/* Subcategories */}
                                {isExpanded && (
                                    <div className="p-4 bg-gray-50" data-testid={`support-master-subcategories-section-${categoryId}`}>
                                        <div className="flex items-center justify-between mb-3">
                                            <h4 className="font-medium text-gray-700" data-testid={`support-master-subcategories-header-${categoryId}`}>{t('supportPage.subcategories_header')}</h4>
                                            {canManage && (
                                            <button
                                                onClick={() => handleAddSubCategory(category)}
                                                disabled={subcategoryLoading}
                                                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-[#4ABDBD] text-white rounded-lg hover:bg-[#3AA8A8] transition-colors disabled:opacity-50"
                                                data-testid={`support-master-add-subcategory-button-${categoryId}`}
                                            >
                                                {subcategoryLoading ? (
                                                    <Loader2 className="animate-spin" size={14} data-testid={`support-master-add-subcategory-loading-icon-${categoryId}`} />
                                                ) : (
                                                    <Plus size={16} data-testid={`support-master-add-subcategory-icon-${categoryId}`} />
                                                )}
                                                {t('supportPage.add_subcategory')}
                                            </button>
                                            )}
                                        </div>
                                        {subcategoryLoading && !categorySubcategories.length ? (
                                            <div className="flex items-center justify-center py-8" data-testid={`support-master-subcategories-loading-${categoryId}`}>
                                                <Loader2 className="animate-spin text-[#4ABDBD]" size={24} data-testid={`support-master-subcategories-loading-icon-${categoryId}`} />
                                            </div>
                                        ) : categorySubcategories.length === 0 ? (
                                            <p className="text-sm text-gray-500 text-center py-4" data-testid={`support-master-no-subcategories-text-${categoryId}`}>
                                                {t('supportPage.no_subcategories')}
                                            </p>
                                        ) : (
                                            <div className="space-y-2" data-testid={`support-master-subcategories-list-${categoryId}`}>
                                                {categorySubcategories.map((subCategory) => {
                                                    const subCategoryId = subCategory.subCategoryId || subCategory.subcategoryId || subCategory.id;
                                                    return (
                                                    <div
                                                        key={subCategoryId}
                                                        className="bg-white p-4 rounded-lg border border-gray-200 flex items-start justify-between hover:border-[#4ABDBD] transition-all"
                                                        data-testid={`support-master-subcategory-card-${subCategoryId}`}
                                                    >
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <h5 className="font-medium text-gray-800" data-testid={`support-master-subcategory-name-${subCategoryId}`}>
                                                                    <MasterLabel
                                                                        type="masterSupportSubCategory"
                                                                        id={subCategory.subCategoryId || subCategory.subcategoryId || subCategory.id}
                                                                        field="name"
                                                                        fallback={subCategory.name}
                                                                    />
                                                                </h5>
                                                                <span data-testid={`support-master-subcategory-viewable-${subCategoryId}`}>
                                                                    <AudienceChips audiences={readAudiences(subCategory)} testIdPrefix={`support-master-subcategory-${subCategoryId}`} />
                                                                </span>
                                                            </div>
                                                            <p className="text-sm text-gray-600 line-clamp-2" data-testid={`support-master-subcategory-description-${subCategoryId}`}>
                                                                <MasterLabel
                                                                    type="masterSupportSubCategory"
                                                                    id={subCategory.subCategoryId || subCategory.subcategoryId || subCategory.id}
                                                                    field="description"
                                                                    fallback={subCategory.description}
                                                                />
                                                            </p>
                                                        </div>
                                                        <div className="flex items-center gap-2 ml-4">
                                                            {canManage && (
                                                            <button
                                                                onClick={() => handleEditSubCategory(subCategory, category)}
                                                                disabled={subcategoryLoading}
                                                                className="p-2 text-[#4ABDBD] hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                                                                title={t('supportPage.edit_subcategory_tooltip')}
                                                                data-testid={`support-master-edit-subcategory-button-${subCategoryId}`}
                                                            >
                                                                <Edit2 size={16} data-testid={`support-master-edit-subcategory-icon-${subCategoryId}`} />
                                                            </button>
                                                            )}
                                                            {canManage && (
                                                            <button
                                                                onClick={() => handleDeleteSubCategory(subCategory, category)}
                                                                disabled={subcategoryLoading}
                                                                className="p-2 text-red-500 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                                                                title={t('supportPage.delete_subcategory_tooltip')}
                                                                data-testid={`support-master-delete-subcategory-button-${subCategoryId}`}
                                                            >
                                                                <Trash2 size={16} data-testid={`support-master-delete-subcategory-icon-${subCategoryId}`} />
                                                            </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Category Modal */}
            {showCategoryModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4" data-testid="support-master-category-modal">
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] sm:max-h-[90vh] flex flex-col" data-testid="support-master-category-modal-content">
                        {/* Header Section - Sticky */}
                        <div className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
                            <h3 className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent" data-testid="support-master-category-modal-title">
                                {editingCategory ? t('supportPage.modal_edit_category') : t('supportPage.modal_add_category')}
                            </h3>
                            <button
                                onClick={() => {
                                    setShowCategoryModal(false);
                                    setCategoryForm(EMPTY_CATEGORY_FORM);
                                    setEditingCategory(null);
                                    setErrors(prev => ({ ...prev, categoryName: '', categoryAudiences: '' }));
                                }}
                                disabled={categoryLoading}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden disabled:opacity-50"
                                data-testid="support-master-category-modal-close-button"
                            >
                                <X className="h-5 w-5 sm:h-6 sm:w-6" data-testid="support-master-category-modal-close-icon" />
                            </button>
                        </div>

                        {/* Scrollable Form Content */}
                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar" data-testid="support-master-category-form">
                            <div className="space-y-4">
                                <div data-testid="support-master-category-name-field-container">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-testid="support-master-category-name-label">
                                        {t('supportPage.category_name_label')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={categoryForm.name}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            // Sanitize input - remove dangerous characters
                                            const sanitized = value.replace(/[<>]/g, '');
                                            // Enforce max length
                                            const finalValue = sanitized.length > 50 ? sanitized.substring(0, 50) : sanitized;
                                            setCategoryForm({ ...categoryForm, name: finalValue });
                                            // Validate
                                            const error = validateCategoryName(finalValue);
                                            setErrors(prev => ({ ...prev, categoryName: error }));
                                        }}
                                        maxLength={50}
                                        className={`w-full p-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] hover:border-[#4ABDBD] text-sm transition-all duration-300 ${
                                            errors.categoryName
                                                ? 'border-red-500 bg-red-50'
                                                : 'border-gray-300'
                                        }`}
                                        placeholder={t('supportPage.category_name_placeholder')}
                                        disabled={categoryLoading}
                                        data-testid="support-master-category-name-input"
                                    />
                                    {errors.categoryName && (
                                        <p className="mt-1 text-xs text-red-500 font-medium" data-testid="support-master-category-name-error">{errors.categoryName}</p>
                                    )}
                                </div>
                                <div data-testid="support-master-category-viewable-field-container">
                                    <label className="block text-sm font-semibold text-gray-700 mb-3" data-testid="support-master-category-viewable-label">
                                        {t('supportPage.who_can_view_category')} <span className="text-red-500">*</span>
                                    </label>
                                    <AudienceCheckboxes
                                        value={categoryForm.audiences}
                                        onChange={(audiences) => {
                                            setCategoryForm({ ...categoryForm, audiences });
                                            setErrors(prev => ({ ...prev, categoryAudiences: audiences.length ? '' : audienceRequiredMessage() }));
                                        }}
                                        allowedTags={AUDIENCE_TAGS}
                                        allowAll
                                        disabled={categoryLoading}
                                        testIdPrefix="support-master-category-audience"
                                    />
                                    {errors.categoryAudiences && (
                                        <p className="mt-2 text-xs text-red-500 font-medium" data-testid="support-master-category-audiences-error">{errors.categoryAudiences}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Footer Section - Sticky */}
                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowCategoryModal(false);
                                    setCategoryForm(EMPTY_CATEGORY_FORM);
                                    setEditingCategory(null);
                                    setErrors(prev => ({ ...prev, categoryName: '', categoryAudiences: '' }));
                                }}
                                disabled={categoryLoading}
                                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer disabled:opacity-50"
                                data-testid="support-master-category-modal-cancel-button"
                            >
                                {t('supportPage.cancel_button')}
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveCategory}
                                disabled={categoryLoading || !!errors.categoryName || !!errors.categoryAudiences}
                                className="px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center gap-2 disabled:opacity-50"
                                data-testid="support-master-category-modal-save-button"
                            >
                                {categoryLoading && <Loader2 className="animate-spin" size={16} data-testid="support-master-category-modal-save-loading-icon" />}
                                {editingCategory ? t('supportPage.update_button') : t('supportPage.create_button')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* SubCategory Modal */}
            {showSubCategoryModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4" data-testid="support-master-subcategory-modal">
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] sm:max-h-[90vh] flex flex-col" data-testid="support-master-subcategory-modal-content">
                        {/* Header Section - Sticky */}
                        <div className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
                            <h3 className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent" data-testid="support-master-subcategory-modal-title">
                                {editingSubCategory ? t('supportPage.modal_edit_subcategory') : t('supportPage.modal_add_subcategory')}
                            </h3>
                            <button
                                onClick={() => {
                                    setShowSubCategoryModal(false);
                                    setSubCategoryForm(EMPTY_SUBCATEGORY_FORM);
                                    setEditingSubCategory(null);
                                    setParentCategoryId(null);
                                    setParentCategory(null);
                                    setErrors({
                                        subCategoryName: '',
                                        subCategoryDescription: '',
                                        subCategoryAudiences: ''
                                    });
                                }}
                                disabled={subcategoryLoading}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden disabled:opacity-50"
                                data-testid="support-master-subcategory-modal-close-button"
                            >
                                <X className="h-5 w-5 sm:h-6 sm:w-6" data-testid="support-master-subcategory-modal-close-icon" />
                            </button>
                        </div>

                        {/* Scrollable Form Content */}
                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar" data-testid="support-master-subcategory-form">
                            <div className="space-y-4">
                                <div data-testid="support-master-subcategory-name-field-container">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-testid="support-master-subcategory-name-label">
                                        {t('supportPage.subcategory_name_label')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={subCategoryForm.name}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            // Sanitize input - remove dangerous characters
                                            const sanitized = value.replace(/[<>]/g, '');
                                            // Enforce max length
                                            const finalValue = sanitized.length > 50 ? sanitized.substring(0, 50) : sanitized;
                                            setSubCategoryForm({ ...subCategoryForm, name: finalValue });
                                            // Validate
                                            const error = validateSubCategoryName(finalValue);
                                            setErrors(prev => ({ ...prev, subCategoryName: error }));
                                        }}
                                        maxLength={50}
                                        className={`w-full p-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] hover:border-[#4ABDBD] text-sm transition-all duration-300 ${
                                            errors.subCategoryName
                                                ? 'border-red-500 bg-red-50'
                                                : 'border-gray-300'
                                        }`}
                                        placeholder={t('supportPage.subcategory_name_placeholder')}
                                        disabled={subcategoryLoading}
                                        data-testid="support-master-subcategory-name-input"
                                    />
                                    {errors.subCategoryName && (
                                        <p className="mt-1 text-xs text-red-500 font-medium" data-testid="support-master-subcategory-name-error">{errors.subCategoryName}</p>
                                    )}
                                </div>
                                <div data-testid="support-master-subcategory-description-field-container">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-testid="support-master-subcategory-description-label">
                                        {t('supportPage.description_label')} <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        value={subCategoryForm.description}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            // Sanitize input - remove dangerous characters
                                            const sanitized = value.replace(/[<>]/g, '');
                                            // Enforce max length
                                            const finalValue = sanitized.length > 200 ? sanitized.substring(0, 200) : sanitized;
                                            setSubCategoryForm({ ...subCategoryForm, description: finalValue });
                                            // Validate
                                            const error = validateSubCategoryDescription(finalValue);
                                            setErrors(prev => ({ ...prev, subCategoryDescription: error }));
                                        }}
                                        rows={6}
                                        maxLength={200}
                                        className={`w-full p-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] hover:border-[#4ABDBD] text-sm transition-all duration-300 resize-none ${
                                            errors.subCategoryDescription
                                                ? 'border-red-500 bg-red-50'
                                                : 'border-gray-300'
                                        }`}
                                        placeholder={t('supportPage.description_placeholder')}
                                        disabled={subcategoryLoading}
                                        data-testid="support-master-subcategory-description-input"
                                    />
                                    {errors.subCategoryDescription && (
                                        <p className="mt-1 text-xs text-red-500 font-medium" data-testid="support-master-subcategory-description-error">{errors.subCategoryDescription}</p>
                                    )}
                                </div>
                                <div data-testid="support-master-subcategory-viewable-field-container">
                                    <label className="block text-sm font-semibold text-gray-700 mb-3" data-testid="support-master-subcategory-viewable-label">
                                        {t('supportPage.who_can_view_subcategory')} <span className="text-red-500">*</span>
                                    </label>
                                    {/* Only the parent's audiences are offered — a sub-category can never
                                        reach someone its category does not (the API enforces the same). */}
                                    <AudienceCheckboxes
                                        value={subCategoryForm.audiences}
                                        onChange={(audiences) => {
                                            setSubCategoryForm({ ...subCategoryForm, audiences });
                                            setErrors(prev => ({ ...prev, subCategoryAudiences: audiences.length ? '' : audienceRequiredMessage() }));
                                        }}
                                        allowedTags={allowedForParent(parentAudiences)}
                                        allowAll={parentAudiences.includes('all')}
                                        disabled={subcategoryLoading}
                                        testIdPrefix="support-master-subcategory-audience"
                                    />
                                    {!parentAudiences.includes('all') && (
                                        <p className="mt-2 text-[11px] text-gray-500" data-testid="support-master-subcategory-audiences-hint">
                                            {t('supportPage.audience_parent_hint', 'Only audiences of the parent category can be chosen.')}
                                        </p>
                                    )}
                                    {errors.subCategoryAudiences && (
                                        <p className="mt-2 text-xs text-red-500 font-medium" data-testid="support-master-subcategory-audiences-error">{errors.subCategoryAudiences}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Footer Section - Sticky */}
                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowSubCategoryModal(false);
                                    setSubCategoryForm(EMPTY_SUBCATEGORY_FORM);
                                    setEditingSubCategory(null);
                                    setParentCategoryId(null);
                                    setParentCategory(null);
                                    setErrors({
                                        subCategoryName: '',
                                        subCategoryDescription: '',
                                        subCategoryAudiences: ''
                                    });
                                }}
                                disabled={subcategoryLoading}
                                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer disabled:opacity-50"
                                data-testid="support-master-subcategory-modal-cancel-button"
                            >
                                {t('supportPage.cancel_button')}
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveSubCategory}
                                disabled={subcategoryLoading || !!errors.subCategoryName || !!errors.subCategoryDescription || !!errors.subCategoryAudiences}
                                className="px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center gap-2 disabled:opacity-50"
                                data-testid="support-master-subcategory-modal-save-button"
                            >
                                {subcategoryLoading && <Loader2 className="animate-spin" size={16} data-testid="support-master-subcategory-modal-save-loading-icon" />}
                                {editingSubCategory ? t('supportPage.update_button') : t('supportPage.create_button')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Upload Modal — single workbook, two sheets (Categories + SubCategories). */}
            {showBulkUploadModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="support-master-bulk-upload-modal">
                    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                <Upload size={22} className="text-[#4ABDBD]" />
                                Bulk Upload Support Categories
                            </h2>
                            <button onClick={handleCloseBulkUpload} className="p-1 hover:bg-gray-100 rounded transition-colors" data-testid="support-master-bulk-upload-modal-close-button">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <h3 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
                                    <AlertCircle size={16} />
                                    Instructions
                                </h3>
                                <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                                    <li>The template has <strong>two sheets</strong>: "Categories" and "SubCategories" — both in one workbook.</li>
                                    <li><strong>Categories sheet</strong>: Name, Audiences.</li>
                                    <li><strong>SubCategories sheet</strong>: CategoryName (must match a Name on the Categories sheet), Name, Description, Audiences (must stay within the parent's audiences).</li>
                                    <li data-testid="support-master-bulk-audiences-hint">{t('supportPage.bulk_audiences_hint', 'Audiences column: comma-separated tags — moc_patient, tenant_patient, doctor, tenant_admin, staff, or all. The legacy values doctor / patient / both are still accepted.')}</li>
                                    <li>Categories are inserted first so sub-category parent lookup by name works in the same upload.</li>
                                    <li>Duplicate names (case-insensitive) are skipped per row.</li>
                                    <li>Maximum file size: 5MB. Accepted formats: .xlsx, .xls.</li>
                                </ul>
                                <button
                                    onClick={handleDownloadSupportSample}
                                    disabled={supportDownloadLoading}
                                    className="mt-3 inline-flex items-center gap-2 text-sm text-blue-700 hover:text-blue-900 font-semibold underline cursor-pointer disabled:opacity-50"
                                    data-testid="support-master-download-sample-button"
                                >
                                    {supportDownloadLoading
                                        ? <span className="border-2 border-blue-700 border-t-transparent rounded-full w-3 h-3 animate-spin"></span>
                                        : <Download size={14} />}
                                    Download sample template
                                </button>
                            </div>

                            {!bulkUploadResult && (
                                <div
                                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 ${bulkSelectedFile ? 'border-[#4ABDBD] bg-[#4ABDBD]/5' : bulkFileError ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD] hover:bg-gray-50'}`}
                                    onDragOver={handleBulkDragOver}
                                    onDrop={handleBulkDrop}
                                    data-testid="support-master-file-dropzone"
                                >
                                    <input
                                        ref={bulkFileInputRef}
                                        type="file"
                                        accept=".xlsx,.xls"
                                        onChange={handleBulkFileSelect}
                                        className="hidden"
                                        id="supportBulkUploadInput"
                                        data-testid="support-master-file-input"
                                    />
                                    {bulkSelectedFile ? (
                                        <div className="flex flex-col items-center gap-3">
                                            <div className="w-16 h-16 bg-[#4ABDBD]/10 rounded-full flex items-center justify-center">
                                                <FileSpreadsheet size={32} className="text-[#4ABDBD]" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-gray-800">{bulkSelectedFile.name}</p>
                                                <p className="text-xs text-gray-500 mt-1">{(bulkSelectedFile.size / 1024).toFixed(1)} KB</p>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    setBulkSelectedFile(null);
                                                    setBulkFileError('');
                                                    setBulkFormatValid(false);
                                                    setBulkFormatChecking(false);
                                                    setBulkFormatInfo(null);
                                                    if (bulkFileInputRef.current) bulkFileInputRef.current.value = '';
                                                }}
                                                className="text-sm text-red-500 hover:text-red-700 font-medium flex items-center gap-1 cursor-pointer"
                                            >
                                                <X size={14} />
                                                Remove file
                                            </button>
                                            {bulkFormatChecking && (
                                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                                    <span className="border-2 border-gray-400 border-t-transparent rounded-full w-3 h-3 animate-spin"></span>
                                                    Checking file format…
                                                </p>
                                            )}
                                            {!bulkFormatChecking && bulkFormatValid && !bulkFileError && (
                                                <div className="text-xs text-green-600 flex flex-col items-center gap-1">
                                                    <p className="flex items-center gap-1">
                                                        <CheckCircle size={12} />
                                                        File format looks good
                                                    </p>
                                                    {bulkFormatInfo && (
                                                        <p className="text-gray-500">
                                                            Detected {bulkFormatInfo.categoriesRowCount || 0} category row(s) and {bulkFormatInfo.subCategoriesRowCount || 0} sub-category row(s)
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <label htmlFor="supportBulkUploadInput" className="cursor-pointer">
                                            <div className="flex flex-col items-center gap-3">
                                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                                                    <Upload size={32} className="text-gray-400" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-700">Drag &amp; drop or click to upload</p>
                                                    <p className="text-xs text-gray-500 mt-1">Accepted formats: .xlsx, .xls (Max 5MB)</p>
                                                </div>
                                            </div>
                                        </label>
                                    )}
                                    {bulkFileError && (
                                        <p className="mt-3 text-sm text-red-500 flex items-center justify-center gap-1">
                                            <AlertCircle size={14} />
                                            {bulkFileError}
                                        </p>
                                    )}
                                </div>
                            )}

                            {bulkUploadResult && (
                                <div className="space-y-4">
                                    {/* Summary — separate counts for categories vs sub-categories. */}
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                        <h3 className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2">
                                            <CheckCircle size={16} />
                                            Upload Completed
                                        </h3>
                                        <div className="space-y-3">
                                            <div>
                                                <p className="text-xs font-semibold text-gray-700 mb-1.5">Categories</p>
                                                <div className="grid grid-cols-3 gap-3">
                                                    <div className="bg-white rounded-lg p-3 text-center border border-green-100">
                                                        <p className="text-xl font-bold text-green-600">{bulkUploadResult.summary?.categoriesCreated || 0}</p>
                                                        <p className="text-xs text-gray-600 mt-0.5">Created</p>
                                                    </div>
                                                    <div className="bg-white rounded-lg p-3 text-center border border-yellow-100">
                                                        <p className="text-xl font-bold text-yellow-600">{bulkUploadResult.summary?.categoriesDuplicates || 0}</p>
                                                        <p className="text-xs text-gray-600 mt-0.5">Duplicates</p>
                                                    </div>
                                                    <div className="bg-white rounded-lg p-3 text-center border border-red-100">
                                                        <p className="text-xl font-bold text-red-600">{bulkUploadResult.summary?.categoriesErrors || 0}</p>
                                                        <p className="text-xs text-gray-600 mt-0.5">Errors</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                <p className="text-xs font-semibold text-gray-700 mb-1.5">Sub-Categories</p>
                                                <div className="grid grid-cols-3 gap-3">
                                                    <div className="bg-white rounded-lg p-3 text-center border border-green-100">
                                                        <p className="text-xl font-bold text-green-600">{bulkUploadResult.summary?.subCategoriesCreated || 0}</p>
                                                        <p className="text-xs text-gray-600 mt-0.5">Created</p>
                                                    </div>
                                                    <div className="bg-white rounded-lg p-3 text-center border border-yellow-100">
                                                        <p className="text-xl font-bold text-yellow-600">{bulkUploadResult.summary?.subCategoriesDuplicates || 0}</p>
                                                        <p className="text-xs text-gray-600 mt-0.5">Duplicates</p>
                                                    </div>
                                                    <div className="bg-white rounded-lg p-3 text-center border border-red-100">
                                                        <p className="text-xl font-bold text-red-600">{bulkUploadResult.summary?.subCategoriesErrors || 0}</p>
                                                        <p className="text-xs text-gray-600 mt-0.5">Errors</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {bulkUploadResult.categories?.created?.length > 0 && (
                                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                                            <div className="bg-green-50 px-4 py-2 border-b border-gray-200">
                                                <p className="text-sm font-semibold text-green-800">Created Categories ({bulkUploadResult.categories.created.length})</p>
                                            </div>
                                            <div className="max-h-32 overflow-y-auto">
                                                {bulkUploadResult.categories.created.map((item, idx) => (
                                                    <div key={idx} className="px-4 py-1.5 text-sm text-gray-700 border-b border-gray-100 last:border-0 flex items-center gap-2">
                                                        <CheckCircle size={12} className="text-green-500 flex-shrink-0" />
                                                        <span className="font-medium">{item.name}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {bulkUploadResult.subCategories?.created?.length > 0 && (
                                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                                            <div className="bg-green-50 px-4 py-2 border-b border-gray-200">
                                                <p className="text-sm font-semibold text-green-800">Created Sub-Categories ({bulkUploadResult.subCategories.created.length})</p>
                                            </div>
                                            <div className="max-h-40 overflow-y-auto">
                                                {bulkUploadResult.subCategories.created.map((item, idx) => (
                                                    <div key={idx} className="px-4 py-1.5 text-sm text-gray-700 border-b border-gray-100 last:border-0 flex items-center gap-2">
                                                        <CheckCircle size={12} className="text-green-500 flex-shrink-0" />
                                                        <span className="font-medium">{item.name}</span>
                                                        <span className="text-xs text-gray-500">under {item.categoryName}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {(bulkUploadResult.categories?.duplicates?.length > 0 || bulkUploadResult.subCategories?.duplicates?.length > 0) && (
                                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                                            <div className="bg-yellow-50 px-4 py-2 border-b border-gray-200">
                                                <p className="text-sm font-semibold text-yellow-800">Skipped Duplicates</p>
                                            </div>
                                            <div className="max-h-40 overflow-y-auto">
                                                {(bulkUploadResult.categories?.duplicates || []).map((item, idx) => (
                                                    <div key={`cd-${idx}`} className="px-4 py-1.5 text-sm text-gray-700 border-b border-gray-100 last:border-0 flex items-center gap-2">
                                                        <AlertCircle size={12} className="text-yellow-500 flex-shrink-0" />
                                                        <span>Categories row {item.row}:</span>
                                                        <span className="font-medium">{item.name}</span>
                                                    </div>
                                                ))}
                                                {(bulkUploadResult.subCategories?.duplicates || []).map((item, idx) => (
                                                    <div key={`sd-${idx}`} className="px-4 py-1.5 text-sm text-gray-700 border-b border-gray-100 last:border-0 flex items-center gap-2">
                                                        <AlertCircle size={12} className="text-yellow-500 flex-shrink-0" />
                                                        <span>SubCategories row {item.row}:</span>
                                                        <span className="font-medium">{item.name}</span>
                                                        <span className="text-xs text-gray-500">under {item.categoryName}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {(bulkUploadResult.categories?.errors?.length > 0 || bulkUploadResult.subCategories?.errors?.length > 0) && (
                                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                                            <div className="bg-red-50 px-4 py-2 border-b border-gray-200">
                                                <p className="text-sm font-semibold text-red-800">Errors</p>
                                            </div>
                                            <div className="max-h-40 overflow-y-auto">
                                                {(bulkUploadResult.categories?.errors || []).map((item, idx) => (
                                                    <div key={`ce-${idx}`} className="px-4 py-1.5 text-sm text-red-700 border-b border-gray-100 last:border-0 flex items-center gap-2">
                                                        <X size={12} className="text-red-500 flex-shrink-0" />
                                                        <span>Categories row {item.row}: {item.name}</span>
                                                        <span className="text-xs text-gray-500">— {item.error}</span>
                                                    </div>
                                                ))}
                                                {(bulkUploadResult.subCategories?.errors || []).map((item, idx) => (
                                                    <div key={`se-${idx}`} className="px-4 py-1.5 text-sm text-red-700 border-b border-gray-100 last:border-0 flex items-center gap-2">
                                                        <X size={12} className="text-red-500 flex-shrink-0" />
                                                        <span>SubCategories row {item.row}: {item.name}</span>
                                                        <span className="text-xs text-gray-500">— {item.error}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex gap-3">
                            <button
                                onClick={handleCloseBulkUpload}
                                disabled={supportBulkUploadLoading}
                                className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {bulkUploadResult ? 'Close' : 'Cancel'}
                            </button>
                            {!bulkUploadResult && (
                                <button
                                    onClick={handleBulkUploadSubmit}
                                    disabled={supportBulkUploadLoading || !bulkSelectedFile || bulkFormatChecking || !bulkFormatValid}
                                    className="flex-1 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="support-master-bulk-upload-submit-button"
                                >
                                    {supportBulkUploadLoading && (
                                        <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                    )}
                                    {supportBulkUploadLoading ? 'Uploading…' : 'Upload File'}
                                </button>
                            )}
                            {bulkUploadResult && (
                                <button
                                    onClick={() => {
                                        setBulkSelectedFile(null);
                                        setBulkUploadResult(null);
                                        setBulkFileError('');
                                        setBulkFormatInfo(null);
                                        if (bulkFileInputRef.current) bulkFileInputRef.current.value = '';
                                    }}
                                    className="flex-1 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-md flex items-center justify-center gap-2"
                                >
                                    <Upload size={16} />
                                    <span>Upload Another File</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
    </div>
  );
};

export default Support;
